import { getBaseUrl, request } from 'src/helper/api';
import type { UserPoolConfig } from 'src/helper/shared-types';

export const getProfile = (token?: string) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/profile`,
      contentType: 'json',
      token,
    },
    'profile/getProfile',
  );

export const syncUser = (token?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/public/organization/validate-sso-user`,
      contentType: 'json',
      token,
    },
    'profile/syncUser',
  );

export const signUp = (userPoolDetails?: UserPoolConfig) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/register`,
      contentType: 'json',
      userPoolDetails,
    },
    'profile/signUp',
  );

export const fetchConfigDetails = (params = {}) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/public/config`,
      data: params,
    },
    'profile/fetchConfigDetails',
  );

export const fetchProviderConfigDetails = (params = {}) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/public/provider/config`,
      data: params,
    },
    'profile/fetchProviderConfigDetails',
  );

export const getOrganization = (token?: string) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/organization`,
      token,
    },
    'profile/getOrganization',
  );

/**
 * `POST /api/organization/update` — the only write path to the organization's
 * name.
 *
 * The controller copies each field across only when the request carries a TRUTHY
 * value, so an empty name is silently ignored rather than clearing the record.
 * Callers must reject an empty name themselves; the server will not.
 */
export const updateOrganization = (token?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/organization/update`,
      token,
      contentType: 'json',
    },
    'profile/updateOrganization',
  );

export const updateTemplate = (token?: string) =>
  request(
    {
      method: 'PUT',
      url: `${getBaseUrl()}api/organization/template`,
      token,
      contentType: 'json',
    },
    'profile/updateTemplate',
  );

export const getTemplate = (token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/organization/templates`,
      token,
      contentType: 'json',
      signal,
    },
    'profile/getTemplate',
  );

export const getSubMenusCount = (token?: string, menuSlug?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/views/count?view_type=${menuSlug}`,
      token,
      contentType: 'json',
      signal,
    },
    'profile/getSubMenusCount',
  );

// notification
export const getNotification = (token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/notifications`,
      contentType: 'json',
      token,
      signal,
    },
    'profile/getNotification',
  );

/**
 * Fetches the server-authoritative unread notifications count
 * (`GET /api/notifications/count`) used to drive the bell badge.
 *
 * Resolves with `{ count: number }`; consumers should always go through
 * the profile slice (`un_read_notification_count`) rather than the raw
 * payload so the optimistic Pusher increments are not stomped on.
 *
 * Accepts an optional `AbortSignal` so background refetches scheduled by
 * `useDebouncedNotificationCountRefetch` can be cancelled on unmount/logout.
 */
export const getNotificationsCount = (token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/notifications/count`,
      contentType: 'json',
      token,
      signal,
    },
    'profile/getNotificationsCount',
  );

export const readNotification = (token?: string, notificationId?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/notifications/${notificationId}/actions/readNotification`,
      contentType: 'json',
      token,
      signal,
    },
    'profile/readNotification',
  );

export const readAllNotification = (token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/notifications/actions/readAllNotification`,
      contentType: 'json',
      token,
      signal,
    },
    'profile/readAllNotification',
  );

export const clearNotification = (token?: string, notificationId?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/notifications/${notificationId}/actions/clearNotification`,
      contentType: 'json',
      token,
      signal,
    },
    'profile/clearNotification',
  );

export const confirmOperationalExceptionUrgentSlot = (
  token?: string,
  claimUuid?: string,
  signal?: AbortSignal,
) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/operational-exception-claims/${claimUuid}/actions/confirm-urgent-slot`,
      contentType: 'json',
      token,
      signal,
    },
    'profile/confirmOperationalExceptionUrgentSlot',
  );

export const unClearNotification = (
  token?: string,
  notificationId?: string,
  signal?: AbortSignal,
) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/notifications/${notificationId}/actions/unClearNotification`,
      contentType: 'json',
      token,
      signal,
    },
    'profile/unClearNotification',
  );

export const clearAllNotification = (token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/notifications/actions/clearAllNotification`,
      contentType: 'json',
      token,
      signal,
    },
    'profile/clearAllNotification',
  );

export const getClearedNotification = (token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/notifications/cleared`,
      contentType: 'json',
      token,
      signal,
    },
    'profile/getClearedNotification',
  );
